import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';
import {
  QueueFileData,
  updateQueueMediaStatus,
} from '../../../redux/reducer/galleryReducer';
import useImages from '../../MainComponent/hooks/useImages';
import { useDispatch } from 'react-redux';
import CustomTypography from '../../../ui/atoms/CustomTypography';
import { ImageStatus } from '../../../constants';
import { file } from 'jszip';

type QueueMediaProps = {
  index: number;
  media: QueueFileData;
  expanded?: boolean;
};

const QueueMediaComponent: React.FC<QueueMediaProps> = ({
  media,
  index,
  expanded,
}) => {
  const { startImageUploadProcess } = useImages();
  const dispatch = useDispatch();
  const initialized = useRef(false); // Track initialization
  const { useImage403ErrorHandler } = useImages();
  const retryImageUpload = () => {
    if (!media) return;
    initialized.current = false;
    dispatch(
      updateQueueMediaStatus({
        guid: media.guid,
        status: 'pending',
        reasons: [],
      })
    );
  };

  useEffect(() => {
    if (initialized.current) return; // Ensure effect runs only once
    initialized.current = true;

    if (!media || media.status !== 'pending') return;

    dispatch(
      updateQueueMediaStatus({
        guid: media.guid,
        status: 'uploading',
        reasons: [],
      })
    );

    startImageUploadProcess(media)
      .then((imageStatusReposnse) => {
        if (imageStatusReposnse?.status === ImageStatus.IMAGE_STATE_UPLOADED) {
          dispatch(
            updateQueueMediaStatus({
              guid: media.guid,
              status: 'uploaded',
              reasons: [],
            })
          );
        }
        if (imageStatusReposnse?.status === ImageStatus.IMAGE_STATE_CREATED) {
          dispatch(
            updateQueueMediaStatus({
              guid: media.guid,
              status: 'created',
              reasons: [],
            })
          );
        }
        if (imageStatusReposnse?.status === ImageStatus.IMAGE_STATE_SUCCESS) {
          dispatch(
            updateQueueMediaStatus({
              guid: media.guid,
              status: 'success',
              reasons: [],
            })
          );
        }
        if (imageStatusReposnse?.status === ImageStatus.IMAGE_STATE_REJECT) {
          dispatch(
            updateQueueMediaStatus({
              guid: media.guid,
              status: 'rejected',
              reasons: imageStatusReposnse?.rejectedReasons || [],
            })
          );
        }
      })
      .catch((error) => {
        dispatch(
          updateQueueMediaStatus({
            guid: media.guid,
            status: 'failed',
            reasons: [],
          })
        );
      });
  }, [dispatch, startImageUploadProcess, media]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        borderRadius: '8px',
        width: expanded ? '200px' : '100px',
        height: expanded ? '200px' : '120px',
        justifyContent: 'center',
        background: '#f0f0f0',
      }}
    >
      <img
        onError={useImage403ErrorHandler}
        src={URL.createObjectURL(media.file)}
        alt={`Uploaded file ${index + 1}`}
        style={{
          width: expanded ? '200px' : '100px',
          height: expanded ? '200px' : '120px',
          borderRadius: '4px',
          objectFit: 'cover',
        }}
      />

      {media.status === 'uploading' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent overlay
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
          }}
        >
          <CircularProgress sx={{ color: '#ffffff' }} />
        </Box>
      )}

      {(media.status === 'failed' ||
        media.status === 'uploaded' ||
        media.status === 'created' ||
        media.status === 'rejected') && (
        <Tooltip
          title={`${
            media.status === 'failed'
              ? 'Upload failed. Click to retry.'
              : media.status === 'rejected'
                ? media.reasons && media.reasons.length > 0
                  ? `Rejected: ${media.reasons.join(', ')}`
                  : 'Upload rejected.'
                : `Status: ${media.status}`
          }${media?.file?.name ? ` File: ${media.file.name}` : ''}`}
          arrow
          placement='top'
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 0, 0, 0.5)', // Red overlay for failure indication
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              gap: 0.3, // Reduce gap for compact layout
              padding: '2px',
            }}
          >
            {/* Failure text (extra small) */}
            <CustomTypography
              variant='body1'
              sx={{
                color: '#ffffff',
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: '10px', // Make it extra small
                lineHeight: 1.2, // Reduce line spacing
              }}
            >
              {media.status}: {media?.file?.name}
            </CustomTypography>

            {media.status === 'failed' && (
              /* Retry Button (Smaller Icon for Compact Space) */
              <IconButton
                sx={{
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                  width: 24, // Smaller button size
                  height: 24,
                }}
                onClick={retryImageUpload}
                aria-label='Re-upload Image'
              >
                <RefreshOutlined fontSize='small' />
              </IconButton>
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// Prevent re-renders unless props deeply change
export default React.memo(
  QueueMediaComponent,
  (prevProps, nextProps) =>
    prevProps.media === nextProps.media &&
    prevProps.index === nextProps.index &&
    prevProps.expanded === nextProps.expanded
);
